import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { usePlayerV2 } from '../stores/usePlayerV2';
import { VIBE_LABELS, VIBE_ORDER, type Vibe } from '../types';
import './routes.css';

type SortKey = 'newest' | 'oldest' | 'title';

export default function LibraryRoute() {
  const stories = useAppStore((s) => s.stories);
  const universes = useAppStore((s) => s.universes);
  const setActiveUniverse = useAppStore((s) => s.setActiveUniverse);
  const setActiveStoryForUniverse = useAppStore((s) => s.setActiveStoryForUniverse);
  const playV2 = usePlayerV2((s) => s.play);
  const currentId = usePlayerV2((s) => s.storyId);
  const status = usePlayerV2((s) => s.status);

  const [universeFilter, setUniverseFilter] = useState<string>('all');
  const [vibeFilter, setVibeFilter] = useState<Vibe | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('newest');

  const nav = useNavigate();

  const completed = useMemo(
    () => Object.values(stories).filter((s) => !s.hidden && s.audio),
    [stories]
  );

  const universeOptions = useMemo(() => {
    const ids = new Set(completed.map((s) => s.universeId));
    return [...ids]
      .filter((id) => universes[id])
      .sort((a, b) => universes[a].name.localeCompare(universes[b].name));
  }, [completed, universes]);

  const vibesPresent = useMemo(() => {
    const set = new Set<Vibe>();
    const pool =
      universeFilter === 'all'
        ? completed
        : completed.filter((s) => s.universeId === universeFilter);
    for (const s of pool) if (s.slug.vibe) set.add(s.slug.vibe);
    return set;
  }, [completed, universeFilter]);

  const filtered = useMemo(() => {
    let out = completed;
    if (universeFilter !== 'all') {
      out = out.filter((s) => s.universeId === universeFilter);
    }
    if (vibeFilter !== 'all') {
      out = out.filter((s) => s.slug.vibe === vibeFilter);
    }
    out = [...out];
    if (sortKey === 'newest') out.sort((a, b) => b.createdAt - a.createdAt);
    else if (sortKey === 'oldest') out.sort((a, b) => a.createdAt - b.createdAt);
    else out.sort((a, b) => a.slug.title.localeCompare(b.slug.title));
    return out;
  }, [completed, universeFilter, vibeFilter, sortKey]);

  function play(storyId: string) {
    const orderedIds = filtered.map((s) => s.id);
    void playV2(storyId, {
      next: (curId) => {
        const i = orderedIds.indexOf(curId);
        if (i < 0 || i >= orderedIds.length - 1) return undefined;
        return orderedIds[i + 1];
      },
    });
  }

  function viewInWorkspace(storyId: string, universeId: string) {
    setActiveUniverse(universeId);
    setActiveStoryForUniverse(universeId, storyId);
    nav('/workspace');
  }

  return (
    <div className="route">
      <h1>Library</h1>

      <div className="card">
        {universeOptions.length > 1 && (
          <div>
            <div className="label">Universe</div>
            <select
              value={universeFilter}
              onChange={(e) => setUniverseFilter(e.target.value)}
            >
              <option value="all">All universes</option>
              {universeOptions.map((id) => (
                <option key={id} value={id}>
                  {universes[id].name}
                </option>
              ))}
            </select>
          </div>
        )}

        {(vibesPresent.size > 0 || vibeFilter !== 'all') && (
          <div>
            <div className="label">Mood</div>
            <div className="vibe-row">
              <button
                className={`vibe-pill ${vibeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setVibeFilter('all')}
              >
                All
              </button>
              {VIBE_ORDER.filter((v) => vibesPresent.has(v)).map((v) => (
                <button
                  key={v}
                  className={`vibe-pill ${vibeFilter === v ? 'active' : ''}`}
                  onClick={() => setVibeFilter(v)}
                >
                  {VIBE_LABELS[v]}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="label">Sort</div>
          <div className="vibe-row">
            <button
              className={`vibe-pill ${sortKey === 'newest' ? 'active' : ''}`}
              onClick={() => setSortKey('newest')}
            >
              Newest
            </button>
            <button
              className={`vibe-pill ${sortKey === 'oldest' ? 'active' : ''}`}
              onClick={() => setSortKey('oldest')}
            >
              Oldest
            </button>
            <button
              className={`vibe-pill ${sortKey === 'title' ? 'active' : ''}`}
              onClick={() => setSortKey('title')}
            >
              Title
            </button>
          </div>
        </div>
      </div>

      {completed.length === 0 ? (
        <div className="dim">No completed stories yet.</div>
      ) : filtered.length === 0 ? (
        <div className="dim">No stories match those filters.</div>
      ) : (
        <div className="queue-list">
          {filtered.map((story) => {
            const isCurrent = currentId === story.id;
            const universe = universes[story.universeId];
            return (
              <div
                key={story.id}
                className={`queue-row ${isCurrent ? 'playing' : ''}`}
              >
                <div className="queue-row-info">
                  <div className="slug-title">
                    {story.slug.title}
                    {story.slug.vibe && (
                      <span className="tag tag-soft vibe-tag">
                        {VIBE_LABELS[story.slug.vibe]}
                      </span>
                    )}
                  </div>
                  {universe && (
                    <div className="dim small">{universe.name}</div>
                  )}
                  <div className="dim small">{story.slug.hook}</div>
                </div>
                <div className="queue-row-actions">
                  <button
                    className="primary"
                    onClick={() => play(story.id)}
                  >
                    {isCurrent && status === 'playing'
                      ? 'Playing'
                      : isCurrent && (status === 'resolving' || status === 'fetching' || status === 'buffering')
                      ? 'Loading…'
                      : 'Play'}
                  </button>
                  <button
                    className="ghost"
                    onClick={() => viewInWorkspace(story.id, story.universeId)}
                  >
                    View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
